import React, { useState, useEffect, useCallback } from 'react';
import { getAllCoupons, generateCoupon, updateCoupon, toggleCouponStatus, deleteCoupon } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';

// MUI Components
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Input,
  Switch,
} from '@mui/material';

// MUI Icons
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocalOffer as LocalOfferIcon,
  Percent as PercentIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface Coupon {
  _id: string;
  name: string;
  code: string;
  discountPercentage: number;
  maxDiscount: number;
  validUntil: string;
  active: boolean;
  createdAt: string;
}

const CouponManagement: React.FC = () => {
  const { admin } = useAuth();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCoupons, setTotalCoupons] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Generate coupon dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    discountPercentage: '',
    maxDiscount: '',
    validUntil: '',
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    code: '',
    discountPercentage: '',
    maxDiscount: '',
    validUntil: '',
  });

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch coupons
  const fetchCoupons = async (search = searchTerm) => {
    try {
      setLoading(true);
      const response = await getAllCoupons({
        search,
        page: page + 1,
        limit: rowsPerPage,
      });
      
      setCoupons(response.coupons);
      setTotalCoupons(response.pagination.total);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  // Create a debounced version of search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      fetchCoupons(searchValue);
    }, 500),
    [page, rowsPerPage]
  );

  // Initial fetch
  useEffect(() => {
    fetchCoupons();
  }, [page, rowsPerPage]);

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open generate coupon dialog
  const handleOpenGenerateDialog = () => {
    setFormData({
      name: '',
      code: '',
      discountPercentage: '',
      maxDiscount: '',
      validUntil: '',
    });
    setFormErrors({
      name: '',
      code: '',
      discountPercentage: '',
      maxDiscount: '',
      validUntil: '',
    });
    setGenerateDialogOpen(true);
  };

  // Close generate coupon dialog
  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
  };

  // Validate generate coupon form
  const validateGenerateForm = (): boolean => {
    const errors = {
      name: '',
      code: '',
      discountPercentage: '',
      maxDiscount: '',
      validUntil: '',
    };
    let isValid = true;

    if (!formData.name) {
      errors.name = 'Coupon name is required';
      isValid = false;
    }

    if (!formData.code) {
      errors.code = 'Coupon code is required';
      isValid = false;
    } else if (formData.code.length < 6) {
      errors.code = 'Coupon code must be at least 6 characters';
      isValid = false;
    }

    if (!formData.discountPercentage) {
      errors.discountPercentage = 'Discount percentage is required';
      isValid = false;
    } else if (Number(formData.discountPercentage) < 1 || Number(formData.discountPercentage) > 100) {
      errors.discountPercentage = 'Discount percentage must be between 1 and 100';
      isValid = false;
    }

    if (!formData.maxDiscount) {
      errors.maxDiscount = 'Maximum discount is required';
      isValid = false;
    } else if (Number(formData.maxDiscount) < 1) {
      errors.maxDiscount = 'Maximum discount must be greater than 0';
      isValid = false;
    }

    if (!formData.validUntil) {
      errors.validUntil = 'Valid until date is required';
      isValid = false;
    } else {
      const selectedDate = new Date(formData.validUntil);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        errors.validUntil = 'Valid until date must be in the future';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle generate coupon
  const handleGenerateCoupon = async () => {
    if (!validateGenerateForm()) return;

    try {
      setIsGenerating(true);
      await generateCoupon({
        name: formData.name,
        code: formData.code.toUpperCase(),
        discountPercentage: Number(formData.discountPercentage),
        maxDiscount: Number(formData.maxDiscount),
        validUntil: formData.validUntil,
      });
      
      toast.success('Coupon generated successfully');
      handleCloseGenerateDialog();
      fetchCoupons(); // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate coupon');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle edit dialog
  const handleOpenEditDialog = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      name: coupon.name,
      code: coupon.code,
      discountPercentage: coupon.discountPercentage.toString(),
      maxDiscount: coupon.maxDiscount.toString(),
      validUntil: new Date(coupon.validUntil).toISOString().split('T')[0],
    });
    setFormErrors({
      name: '',
      code: '',
      discountPercentage: '',
      maxDiscount: '',
      validUntil: '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCoupon(null);
  };

  // Handle update coupon
  const handleUpdateCoupon = async () => {
    if (!selectedCoupon || !validateGenerateForm()) return;

    try {
      setIsUpdating(true);
      await updateCoupon(selectedCoupon._id, {
        name: formData.name,
        code: formData.code.toUpperCase(),
        discountPercentage: Number(formData.discountPercentage),
        maxDiscount: Number(formData.maxDiscount),
        validUntil: formData.validUntil,
      });
      
      toast.success('Coupon updated successfully');
      handleCloseEditDialog();
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update coupon');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (coupon: Coupon) => {
    try {
      await toggleCouponStatus(coupon._id, !coupon.active);
      toast.success(`Coupon ${!coupon.active ? 'activated' : 'deactivated'} successfully`);
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle coupon status');
    }
  };

  // Handle delete dialog
  const handleOpenDeleteDialog = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCoupon(null);
  };

  // Handle delete coupon
  const handleDeleteCoupon = async () => {
    if (!selectedCoupon) return;

    try {
      setIsDeleting(true);
      await deleteCoupon(selectedCoupon._id);
      toast.success('Coupon deleted successfully');
      handleCloseDeleteDialog();
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete coupon');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, color: '#0F0F2D', mb: 4 }}>
        Coupon Management
      </Typography>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              placeholder="Search coupons by code..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleOpenGenerateDialog}
              fullWidth
              sx={{
                backgroundColor: '#7065F0',
                '&:hover': {
                  backgroundColor: '#5A52C4',
                },
              }}
            >
              Generate Coupon
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress size={60} sx={{ color: '#7065F0' }} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && coupons.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <LocalOfferIcon sx={{ fontSize: 60, color: '#9CA3AF', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
            No coupons found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate your first coupon by clicking the button below
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenGenerateDialog}
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            Generate Coupon
          </Button>
        </Paper>
      )}

      {/* Coupons Table */}
      {!loading && coupons.length > 0 && (
        <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#F7F7FD' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Max Discount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Valid Until</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((coupon) => (
                <TableRow key={coupon._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {coupon.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {coupon.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {coupon.discountPercentage}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      ₹{coupon.maxDiscount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(coupon.validUntil).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(coupon.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={coupon.active}
                      onChange={() => handleToggleStatus(coupon)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(coupon)}
                      sx={{ color: '#7065F0', mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(coupon)}
                      sx={{ color: '#EF4444' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCoupons}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Edit Coupon Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Coupon</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Coupon Name"
                  name="name"
                  fullWidth
                  value={formData.name}
                  onChange={handleFormChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Coupon Code"
                  name="code"
                  fullWidth
                  value={formData.code}
                  onChange={handleFormChange}
                  error={!!formErrors.code}
                  helperText={formErrors.code || 'Enter a unique code (min. 6 characters)'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: { textTransform: 'uppercase' }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Discount Percentage"
                  name="discountPercentage"
                  type="number"
                  fullWidth
                  value={formData.discountPercentage}
                  onChange={handleFormChange}
                  error={!!formErrors.discountPercentage}
                  helperText={formErrors.discountPercentage}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Maximum Discount Amount (₹)"
                  name="maxDiscount"
                  type="number"
                  fullWidth
                  value={formData.maxDiscount}
                  onChange={handleFormChange}
                  error={!!formErrors.maxDiscount}
                  helperText={formErrors.maxDiscount}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Valid Until"
                  name="validUntil"
                  type="date"
                  fullWidth
                  value={formData.validUntil}
                  onChange={handleFormChange}
                  error={!!formErrors.validUntil}
                  helperText={formErrors.validUntil || 'Select a future date'}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0], // Set minimum date to today
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateCoupon}
            variant="contained"
            disabled={isUpdating}
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            {isUpdating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Updating...
              </>
            ) : (
              'Update'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Coupon</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this coupon? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCoupon}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Coupon Dialog */}
      <Dialog open={generateDialogOpen} onClose={handleCloseGenerateDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Generate New Coupon</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Coupon Name"
                  name="name"
                  fullWidth
                  value={formData.name}
                  onChange={handleFormChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Coupon Code"
                  name="code"
                  fullWidth
                  value={formData.code}
                  onChange={handleFormChange}
                  error={!!formErrors.code}
                  helperText={formErrors.code || 'Enter a unique code (min. 6 characters)'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: { textTransform: 'uppercase' }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Discount Percentage"
                  name="discountPercentage"
                  type="number"
                  fullWidth
                  value={formData.discountPercentage}
                  onChange={handleFormChange}
                  error={!!formErrors.discountPercentage}
                  helperText={formErrors.discountPercentage}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Maximum Discount Amount (₹)"
                  name="maxDiscount"
                  type="number"
                  fullWidth
                  value={formData.maxDiscount}
                  onChange={handleFormChange}
                  error={!!formErrors.maxDiscount}
                  helperText={formErrors.maxDiscount}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Valid Until"
                  name="validUntil"
                  type="date"
                  fullWidth
                  value={formData.validUntil}
                  onChange={handleFormChange}
                  error={!!formErrors.validUntil}
                  helperText={formErrors.validUntil || 'Select a future date'}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0], // Set minimum date to today
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseGenerateDialog} color="inherit">Cancel</Button>
          <Button 
            onClick={handleGenerateCoupon} 
            variant="contained"
            disabled={isGenerating}
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            {isGenerating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Generating...
              </>
            ) : (
              'Generate'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CouponManagement; 